import { GOOGLE_PAY_ENV } from "constants/common";
import { useEffect, useRef, useState } from "react";
import { useRootSelector } from "store";

declare global {
  interface Window {
    google: any;
  }
}

export function useGooglePay(amount: number) {
  const { client: clientConfig } = useRootSelector((state) => state.config);
  const environment =
    GOOGLE_PAY_ENV === "PRODUCTION" ? "PRODUCTION" : "TEST";

  const baseAllowedPaymentMethod = {
    type: "CARD",
    parameters: {
      // PAN_ONLY is required for broad card compatibility (desktop and non-tokenized cards).
      allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
      allowedCardNetworks: ["AMEX", "DISCOVER", "JCB", "MASTERCARD", "VISA"],
    },
  };

  const cardAllowedPaymentMethod = {
    ...baseAllowedPaymentMethod,
    tokenizationSpecification: {
      // type: "PAYMENT_GATEWAY",
      // parameters: {
      //   gateway: "example", // Use your gateway name, e.g., "stripe"
      //   gatewayMerchantId: "exampleGatewayMerchantId", // Use your merchant id for gateway
      // },
      type: "DIRECT",
      parameters: {
        protocolVersion: "ECv2",
        publicKey:
          "BFn5ykKGIus2OMfiuvgOiCE2Zey7XgAhm5cufdFVKNQNh+uAoi9Lp73Y4IwnVyesP8+trbxfRjLJjC0D1Fho6Xc=", // Env specific?
      },
    },
  };

  const merchantInfo = {
    merchantId: "BCR2DN7T5HU4ZASX",
    merchantName: clientConfig.name || "",
  };

  const config = {
    environment,
    merchantInfo: merchantInfo,
    allowedPaymentMethods: [baseAllowedPaymentMethod],
    paymentDataRequest: {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [cardAllowedPaymentMethod],
      merchantInfo: merchantInfo,
      transactionInfo: {
        totalPriceStatus: "ESTIMATED", // supposed to work and say "Continue" in google popup
        totalPrice: amount.toFixed(2),
        currencyCode: "USD",
        countryCode: "US",
        checkoutOption: "DEFAULT",
      },
    },
    buttonOptions: {
      onClick: undefined, // We'll set this below
      buttonType: "short",
      buttonColor: "default",
    },
  };
  const [ready, setReady] = useState(false);
  const paymentsClientRef = useRef<any>(null);

  useEffect(() => {
    // Load Google Pay script if not present
    if (!window.google) {
      const script = document.createElement("script");
      script.src = "https://pay.google.com/gp/p/js/pay.js";
      script.async = true;
      script.onload = () => {
        initializeGooglePay();
      };
      document.body.appendChild(script);
    } else {
      initializeGooglePay();
    }

    function initializeGooglePay() {
      paymentsClientRef.current = new window.google.payments.api.PaymentsClient(
        {
          environment: config.environment,
        },
      );

      paymentsClientRef.current
        .isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: config.allowedPaymentMethods,
        })
        .then((response: { result: boolean }) => {
          setReady(response.result);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to trigger the Google Pay sheet
  const loadPaymentData = () => {
    if (!paymentsClientRef.current) return;
    return paymentsClientRef.current.loadPaymentData(config.paymentDataRequest);
  };

  return { ready, paymentsClient: paymentsClientRef.current, loadPaymentData };
}
